import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface OverviewSectionProps {
  onNavigate: (section: string) => void;
}

const quickActions = [
  { label: 'Explore Automations', icon: 'ri-flashlight-line', section: 'automations', desc: 'View available AI automation services' },
  { label: 'Book a Demo', icon: 'ri-calendar-2-line', section: 'booking', desc: 'Schedule a personalised walkthrough' },
  { label: 'View CRM & Leads', icon: 'ri-contacts-book-2-line', section: 'crm', desc: 'Manage your leads pipeline' },
  { label: 'Account Settings', icon: 'ri-settings-3-line', section: 'settings', desc: 'Update your profile and preferences' },
];

const OverviewSection = ({ onNavigate }: OverviewSectionProps) => {
  const { user, profile } = useAuth();
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(true);

  useEffect(() => {
    const fetchMemberCount = async () => {
      setCountLoading(true);
      const { count, error } = await supabase
        .from('signups')
        .select('*', { count: 'exact', head: true });
      if (!error && count !== null) {
        setMemberCount(count);
      }
      setCountLoading(false);
    };
    fetchMemberCount();
  }, []);

  const firstName = profile?.first_name
    || user?.user_metadata?.first_name
    || user?.email?.split('@')[0]
    || 'there';

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const stats = [
    {
      label: 'Total Members',
      value: countLoading ? null : (memberCount !== null ? memberCount.toLocaleString() : '—'),
      icon: 'ri-group-line',
      color: 'text-ea-blue',
      bg: 'bg-ea-blue/10 border-ea-blue/20',
      note: 'Registered signups',
      live: true,
    },
    {
      label: 'Active Automations',
      value: '—',
      icon: 'ri-flashlight-line',
      color: 'text-violet-400',
      bg: 'bg-violet-400/10 border-violet-400/20',
      note: 'Coming soon',
      live: false,
    },
    {
      label: 'Leads Captured',
      value: '—',
      icon: 'ri-user-add-line',
      color: 'text-green-400',
      bg: 'bg-green-400/10 border-green-400/20',
      note: 'Connect CRM',
      live: false,
    },
    {
      label: 'Demos Booked',
      value: '—',
      icon: 'ri-calendar-check-line',
      color: 'text-amber-400',
      bg: 'bg-amber-400/10 border-amber-400/20',
      note: 'Book below',
      live: false,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome banner */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-ea-navy via-ea-dark-card to-ea-navy border border-ea-dark-border p-8">
        <div className="absolute inset-0 bg-gradient-to-r from-ea-blue/5 via-transparent to-ea-blue/5 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-ea-blue/5 blur-3xl pointer-events-none"></div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-green-400 text-xs font-medium">Member Dashboard Active</span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              {greeting}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric capitalize">{firstName}</span> 👋
            </h1>
            <p className="text-ea-gray text-sm max-w-lg">
              Welcome to your Everything Automation members portal. Track your automations, manage leads, and access exclusive member features.
            </p>
          </div>
          {/* Live member count badge in banner */}
          <div className="shrink-0 flex flex-col items-center justify-center bg-ea-blue/10 border border-ea-blue/25 rounded-2xl px-7 py-5 min-w-[130px]">
            {countLoading ? (
              <div className="w-12 h-7 bg-ea-dark-border animate-pulse rounded-md mb-1"></div>
            ) : (
              <p className="text-3xl font-bold text-white leading-none">
                {memberCount !== null ? memberCount.toLocaleString() : '—'}
              </p>
            )}
            <p className="text-ea-blue text-xs font-medium mt-1 whitespace-nowrap">Total Members</p>
            <div className="flex items-center gap-1 mt-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue animate-pulse"></span>
              <span className="text-ea-gray/60 text-[10px]">Live count</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className={`bg-ea-dark-card border ${stat.bg} rounded-xl p-5`}>
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${stat.bg} border mb-3`}>
              <i className={`${stat.icon} ${stat.color} text-base`}></i>
            </div>
            {stat.value === null ? (
              <div className="w-10 h-6 bg-ea-dark-border animate-pulse rounded mb-1"></div>
            ) : (
              <p className="text-2xl font-bold text-white mb-0.5">{stat.value}</p>
            )}
            <p className="text-ea-gray text-xs font-medium">{stat.label}</p>
            <div className="flex items-center gap-1.5 mt-1">
              {stat.live && (
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue animate-pulse shrink-0"></span>
              )}
              <p className="text-ea-gray/50 text-[10px]">{stat.note}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-white font-bold text-base mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => onNavigate(action.section)}
              className="flex items-start gap-4 p-5 bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/40 rounded-xl transition-all duration-200 cursor-pointer text-left group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 shrink-0 group-hover:bg-ea-blue/20 transition-colors">
                <i className={`${action.icon} text-ea-blue text-base`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold mb-0.5 group-hover:text-ea-blue transition-colors">{action.label}</p>
                <p className="text-ea-gray text-xs leading-relaxed">{action.desc}</p>
              </div>
              <div className="w-4 h-4 flex items-center justify-center ml-auto shrink-0">
                <i className="ri-arrow-right-line text-ea-gray group-hover:text-ea-blue text-sm transition-colors"></i>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <h2 className="text-white font-bold text-base mb-4">Recent Activity</h2>
        <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl divide-y divide-ea-dark-border/50">
          <div className="px-5 py-4 flex items-center gap-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 shrink-0">
              <i className="ri-user-add-line text-green-400 text-sm"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium">Account Created</p>
              <p className="text-ea-gray/60 text-xs">Your member account is active and ready</p>
            </div>
            <span className="text-ea-gray/50 text-xs whitespace-nowrap">Just now</span>
          </div>
          <div className="px-5 py-4 flex items-center gap-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/20 shrink-0">
              <i className="ri-login-circle-line text-ea-blue text-sm"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium">Signed In</p>
              <p className="text-ea-gray/60 text-xs">Welcome back to your dashboard</p>
            </div>
            <span className="text-ea-gray/50 text-xs whitespace-nowrap">Now</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
